import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useImpersonation } from '@/contexts/ImpersonationContext';

interface UserPermission {
  resource: string;
  create: boolean;
  read: boolean;
  update: boolean;
  delete: boolean;
  scope: 'all' | 'department' | 'team' | 'assigned' | 'own' | 'related' | 'none';
}

interface UserPermissions {
  clients: UserPermission['create' | 'read' | 'update' | 'delete' | 'scope'];
  cases: UserPermission['create' | 'read' | 'update' | 'delete' | 'scope'];
  leads: UserPermission['create' | 'read' | 'update' | 'delete' | 'scope'];
  documents: UserPermission['create' | 'read' | 'update' | 'delete' | 'scope'];
  payments: UserPermission['create' | 'read' | 'update' | 'delete' | 'scope'];
  catalog: UserPermission['create' | 'read' | 'update' | 'delete' | 'scope'];
}

export const useUserPermissions = () => {
  const { user } = useAuth();
  const { impersonatedUserId } = useImpersonation();

  const { data: permissions, isLoading } = useQuery({
    queryKey: ['user-permissions', impersonatedUserId || user?.id, !!impersonatedUserId],
    queryFn: async () => {
      if (impersonatedUserId) {
        // Fetch permissions for impersonated user by app_user.id
        const { data, error } = await supabase
          .from('app_users')
          .select(`
            *,
            system_role:system_roles(id, name, code, permissions),
            department:departments(id, name, code),
            accessible_departments:user_department_access(
              department:departments(id, name, code)
            )
          `)
          .eq('id', impersonatedUserId)
          .maybeSingle();

        if (error) throw error;
        return data;
      }

      if (!user) return null;

      const { data, error } = await supabase
        .from('app_users')
        .select(`
          *,
          system_role:system_roles(id, name, code, permissions),
          department:departments(id, name, code),
          accessible_departments:user_department_access(
            department:departments(id, name, code)
          )
        `)
        .eq('auth_user_id', user.id)
        .not('system_role_id', 'is', null)
        .not('auth_user_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!impersonatedUserId || !!user,
  });

  const can = (resource: keyof UserPermissions, action: 'create' | 'read' | 'update' | 'delete'): boolean => {
    if (!permissions?.system_role) return false;
    const resourcePerms = permissions.system_role.permissions[resource];
    if (!resourcePerms) return false;
    return resourcePerms[action] === true;
  };

  const getScope = (resource: keyof UserPermissions): string => {
    if (!permissions?.system_role) return 'none';
    const resourcePerms = permissions.system_role.permissions[resource];
    return resourcePerms?.scope || 'none';
  };

  const canViewRecord = (resource: keyof UserPermissions, ownerId: string | null): boolean => {
    const scope = getScope(resource);
    const currentUserId = permissions?.id;
    
    if (scope === 'all') return true;
    if (scope === 'none') return false;
    
    if (scope === 'own_or_unassigned') {
      return ownerId === currentUserId || ownerId === null;
    }
    
    if (scope === 'own') {
      return ownerId === currentUserId;
    }
    
    return false;
  };

  return {
    permissions,
    isLoading,
    can,
    getScope,
    canViewRecord,
    isAdmin: permissions?.system_role?.code === 'admin' || permissions?.system_role?.code === 'it',
    isSalesManager: permissions?.system_role?.code === 'sales_manager',
    isHeadOfOperations: permissions?.system_role?.code === 'head_operations',
    userDepartment: permissions?.department,
    accessibleDepartments: permissions?.accessible_departments?.map((ad: any) => ad.department) || [],
  };
};
